/**
 * `GET /workflows/test-executions/trend` — one bar per run over a date window,
 * with native failures split by failure-diagnosis category. Feeds the
 * runs-list trend chart; see `testRunsTrendChartData.ts` for the pure data
 * shaping and `TestRunsTrendChart.tsx` for the rendering.
 *
 * @module features/workflows/useTestRunsTrend
 */
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import type { TestRunsTrendPoint } from '../../ui/pages/runs/test-runs/testRunsTrendChartData';
import { applyTestRunsFilterParams, type TestRunsFilterParams } from './testRunsFilters';

export type TestRunsTrendWindow = {
  since: string;
  /** True when the window held more runs than the server will return. */
  truncated: boolean;
  /** Total runs matching the window/filters, before the server's cap was applied. */
  totalItems: number;
};

export type TestRunsTrendResponse = {
  ok: boolean;
  data: TestRunsTrendPoint[];
  window: TestRunsTrendWindow;
};

export type TestRunsTrendFetch = (
  input: Parameters<typeof fetch>[0],
  init?: Parameters<typeof fetch>[1]
) => ReturnType<typeof fetch>;

type FetchTestRunsTrendOptions = {
  apiUrl?: string;
  fetchFn?: TestRunsTrendFetch;
  days?: number;
  filters?: TestRunsFilterParams;
};

export async function fetchTestRunsTrend({
  apiUrl = API_URL,
  fetchFn = fetch,
  days,
  filters,
}: FetchTestRunsTrendOptions): Promise<TestRunsTrendResponse> {
  const url = new URL(`${apiUrl}/workflows/test-executions/trend`);
  if (days) url.searchParams.set('days', String(days));
  applyTestRunsFilterParams(url, filters);

  const response = await fetchFn(url.toString(), { credentials: 'include' });
  if (!response.ok) {
    throw await toApiError(response, 'Failed to load the test-runs trend');
  }
  return (await response.json()) as TestRunsTrendResponse;
}

export function useTestRunsTrend(
  orgId: string | null,
  options?: { days?: number; filters?: TestRunsFilterParams }
) {
  const days = options?.days ?? 30;
  const filters = options?.filters;

  return useQuery<TestRunsTrendResponse>({
    queryKey: ['test-runs-trend', orgId, days, filters],
    enabled: Boolean(orgId),
    queryFn: () => fetchTestRunsTrend({ days, filters }),
  });
}
